import { Controller } from "@hotwired/stimulus"

// Drag-and-drop / click-to-browse file upload. The controller is on the <form>; on drop or
// file selection it puts the file into the hidden <input type=file> and submits the form.
// The server converts the upload (see UploadedDocument). Shared by the Meeting Notes
// (FD-518) and Pages (FD-549) doc-import zones — fetch-free plain multipart, matching the
// app's no-direct-upload convention.
//
// A .md import is instant, but converting a spreadsheet takes seconds, during which the zone
// looked idle and invited a duplicate drop (and a duplicate import). Hence the busy state.
//
// Busy is cleared on `turbo:submit-end`, NOT in connect(). Every failure branch of both
// controllers redirects back to the SAME url the form posted from, which Turbo treats as a
// same-location redirect and — with the app-wide `turbo_refreshes_with method: :morph` —
// renders by morphing. Idiomorph patches the existing form/button in place rather than
// replacing them, so the Stimulus element is never removed and connect() never runs again:
// the morph strips the busy CLASSES (they aren't in the server HTML) while leaving the
// `submitting` flag set, and the zone silently ignores every later drop while looking
// perfectly normal. `turbo:submit-end` fires exactly once per submission regardless of how
// Turbo renders the response. peek_controller and modal_controller work around the same
// morph-preserves-the-node behavior the same way.
//
// FD-1354: a work item's paperclip is a zone too, around a cs-button. A zone that holds one
// names it as the `button` target, and the button shows its own `loading` state (a spinner
// that also ignores clicks) while the files upload, in place of the zone's dimming.
export default class extends Controller {
  static targets = ["input", "zone", "button"]

  connect() {
    this.reset()
    this.onSubmitEnd = () => this.reset()
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  reset() {
    this.submitting = false
    // FD-1354: let go of the files just sent. A refresh morph keeps this input, so the same
    // file picked again (a retry after a refused upload, say) changed nothing and sent nothing.
    if (this.hasInputTarget) this.inputTarget.value = ""
    if (this.hasButtonTarget) this.buttonTarget.loading = false
    if (!this.hasZoneTarget) return
    this.zoneTarget.classList.remove("opacity-60")
    this.zoneTarget.removeAttribute("aria-busy")
  }

  browse() {
    if (this.submitting) return
    this.inputTarget.click()
  }

  over(event) {
    // preventDefault unconditionally, including while busy. Skipping it — or removing the
    // zone from hit-testing with pointer-events-none — means no ancestor claims the drag, and
    // the browser's own default file-drop behavior takes over and navigates the tab away from
    // the app to display the dropped file.
    event.preventDefault()
    if (this.submitting) return
    this.zoneTarget.classList.add("border-outline-strong", "bg-surface-low")
  }

  leave() {
    this.zoneTarget.classList.remove("border-outline-strong", "bg-surface-low")
  }

  drop(event) {
    event.preventDefault()
    this.leave()
    if (this.submitting) return

    const files = event.dataTransfer && event.dataTransfer.files
    if (!files || files.length === 0) return
    this.inputTarget.files = files
    this.submit()
  }

  submit() {
    if (this.submitting || this.inputTarget.files.length === 0) return
    this.submitting = true
    if (this.hasButtonTarget) {
      this.buttonTarget.loading = true
    } else if (this.hasZoneTarget) {
      this.zoneTarget.classList.add("opacity-60")
      this.zoneTarget.setAttribute("aria-busy", "true")
    }
    this.element.requestSubmit()
  }
}
